
import type {
  SearcherRegistration,
  SearchResult,
  SearchResultSection,
} from "@pineseed/gui/types";

/**
 * Search Manager
 * STRICT RULE: Contains ONLY business logic - no UI imports, no direct atom mutations
 * Manages registration and coordination of individual searchers
 */

export class SearchManager {
  private static instance: SearchManager;
  private searchers = new Map<string, SearcherRegistration>();

  public static getInstance(): SearchManager {
    if (!SearchManager.instance) {
      SearchManager.instance = new SearchManager();
    }
    return SearchManager.instance;
  }

  /**
   * Register a searcher
   */
  public registerSearcher(registration: SearcherRegistration): void {
    this.searchers.set(registration.config.type, registration);
  }

  /**
   * Unregister a searcher
   */
  public unregisterSearcher(type: string): void {
    this.searchers.delete(type);
  }

  /**
   * Get all registered searchers
   */
  public getSearchers(): SearcherRegistration[] {
    return Array.from(this.searchers.values()).filter((s) => s.config.enabled);
  }

  /**
   * Search across all registered searchers
   */
  public async searchAll(query: string): Promise<SearchResultSection[]> {
    if (!query.trim()) {
      return [];
    }

    const enabledSearchers = this.getSearchers();
    const sections: SearchResultSection[] = [];

    // Create initial sections with loading state
    for (const searcher of enabledSearchers) {
      sections.push({
        searcherType: searcher.config.type,
        title: searcher.config.title,
        results: [],
        isLoading: true,
      });
    }

    // Execute searches in parallel
    const searchPromises = enabledSearchers.map(async (searcher, index) => {
      try {
        const results = await searcher.search(query);
        sections[index] = {
          ...sections[index],
          results,
          isLoading: false,
        };
      } catch (error) {
        sections[index] = {
          ...sections[index],
          results: [],
          isLoading: false,
          error: error instanceof Error ? error.message : "Search failed",
        };
      }
    });

    await Promise.all(searchPromises);

    // Filter out sections with no results (unless they have errors)
    return sections.filter(
      (section) => section.results.length > 0 || section.error,
    );
  }

  /**
   * Search with a specific searcher
   */
  public async searchWithSearcher(
    type: string,
    query: string,
  ): Promise<SearchResult[]> {
    const searcher = this.searchers.get(type);
    if (!searcher || !searcher.config.enabled) {
      return [];
    }

    try {
      return await searcher.search(query);
    } catch (error) {
      console.error(`Search failed for searcher ${type}:`, error);
      return [];
    }
  }

  /**
   * Get searcher configuration
   */
  public getSearcherConfig(type: string) {
    const searcher = this.searchers.get(type);
    return searcher?.config;
  }

  /**
   * Enable/disable a searcher
   */
  public setSearcherEnabled(type: string, enabled: boolean): void {
    const searcher = this.searchers.get(type);
    if (searcher) {
      searcher.config.enabled = enabled;
    }
  }
}

// Singleton instance
export const searchManager = SearchManager.getInstance();


